import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, User, Activity, Clock, MapPin,
  CheckCircle, XCircle, AlertTriangle, Calendar,
  Download, Filter, RefreshCw
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * QuantumFlowSync.AI - Employee Activity History Page
 * 
 * Features:
 * - View all activity for a specific employee
 * - Timeline view of actions
 * - Filter by action type and date range
 * - Export employee activity report
 * - Visual activity patterns
 */

const EmployeeActivityPage = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { apiRequest } = useAuth();
  
  // State
  const [employee, setEmployee] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [filters, setFilters] = useState({
    action: '',
    start_date: '',
    end_date: ''
  });
  
  const [showFilters, setShowFilters] = useState(false);
  
  // Fetch employee details
  const fetchEmployee = async () => {
    try {
      const response = await apiRequest(`/api/admin/employees/${employeeId}`);
      
      if (response.success) {
        setEmployee(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch employee:', err);
    }
  };
  
  // Fetch employee activities
  const fetchActivities = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50'
      });
      
      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });
      
      const response = await apiRequest(
        `/api/admin/audit-logs/employee/${employeeId}?${params.toString()}`
      );
      
      if (response.success) {
        setActivities(response.data.logs);
        setTotalPages(response.data.total_pages);
      } else {
        setError(response.error || 'Failed to fetch activities');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch activities');
    } finally {
      setLoading(false);
    }
  };
  
  // Export activity report
  const exportReport = async (format) => {
    try {
      const params = new URLSearchParams({ format });
      
      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });
      
      const response = await apiRequest(
        `/api/admin/audit-logs/employee/${employeeId}/export?${params.toString()}`
      );
      
      if (response.success) {
        // Download file
        const blob = new Blob([response.data], { 
          type: format === 'csv' ? 'text/csv' : 'application/json' 
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `employee-${employeeId}-activity-${new Date().toISOString()}.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Export failed:', err);
    }
  };
  
  // Effect: Fetch employee and activities on mount
  useEffect(() => {
    fetchEmployee();
    fetchActivities();
  }, [employeeId, page, filters]);
  
  // Render result icon
  const renderResultIcon = (result) => {
    const icons = {
      success: { Icon: CheckCircle, color: 'text-green-600' },
      denied: { Icon: XCircle, color: 'text-red-600' },
      failure: { Icon: AlertTriangle, color: 'text-yellow-600' }
    };
    
    const config = icons[result] || icons.failure;
    const Icon = config.Icon;
    
    return <Icon className={`w-5 h-5 ${config.color}`} />;
  };
  
  // Render action badge
  const renderActionBadge = (action) => {
    const colors = {
      login: 'bg-blue-100 text-blue-800',
      logout: 'bg-gray-100 text-gray-800',
      create_employee: 'bg-green-100 text-green-800',
      update_employee: 'bg-yellow-100 text-yellow-800',
      delete_employee: 'bg-red-100 text-red-800',
      assign_role: 'bg-purple-100 text-purple-800',
      revoke_role: 'bg-orange-100 text-orange-800',
      permission_check: 'bg-indigo-100 text-indigo-800'
    };
    
    const color = colors[action] || 'bg-gray-100 text-gray-800';
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
        {action.replace(/_/g, ' ').toUpperCase()}
      </span>
    );
  };
  
  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/employees')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Employees
        </button>
        
        {employee && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-indigo-600" />
                </div>
                <div className="ml-4">
                  <h1 className="text-2xl font-bold text-gray-900">{employee.name}</h1>
                  <p className="text-sm text-gray-500">{employee.email}</p>
                  <div className="flex items-center mt-2 space-x-4">
                    <span className="text-xs text-gray-500">
                      ID: {employee.id}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      employee.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {employee.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="text-right">
                <p className="text-sm text-gray-500">Activity History</p>
                <p className="text-2xl font-bold text-gray-900">{activities.length}</p>
                <p className="text-xs text-gray-500 mt-1">actions tracked</p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Toolbar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Activity className="w-5 h-5 mr-2 text-indigo-600" />
            Activity Timeline
          </h2>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg flex items-center ${
                showFilters 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </button>
            
            <button
              onClick={fetchActivities}
              disabled={loading}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            
            <div className="relative group">
              <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center">
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
              <div className="absolute right-0 mt-2 w-32 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                <button
                  onClick={() => exportReport('csv')}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Export CSV
                </button>
                <button
                  onClick={() => exportReport('json')}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Export JSON
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
              <select
                value={filters.action}
                onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Actions</option>
                <option value="login">Login</option>
                <option value="logout">Logout</option>
                <option value="create_employee">Create Employee</option>
                <option value="update_employee">Update Employee</option>
                <option value="delete_employee">Delete Employee</option>
                <option value="assign_role">Assign Role</option>
                <option value="revoke_role">Revoke Role</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="datetime-local"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="datetime-local"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        )}
      </div>
      
      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}
      
      {/* Activity Timeline */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="p-12 text-center text-gray-500">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3" />
            Loading activity history...
          </div>
        ) : activities.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            No activity found
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {activities.map((activity) => (
              <div key={activity.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start">
                  {/* Timeline dot */}
                  <div className="flex-shrink-0 mr-4">
                    {renderResultIcon(activity.result)}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div>
                        {renderActionBadge(activity.action)}
                        <span className="ml-2 text-sm text-gray-500">
                          on <span className="font-medium text-gray-900">{activity.resource}</span>
                          {activity.resource_id && (
                            <span className="text-gray-500"> #{activity.resource_id}</span>
                          )}
                        </span>
                      </div>
                      
                      <div className="flex items-center text-xs text-gray-500">
                        <Clock className="w-3 h-3 mr-1" />
                        {new Date(activity.timestamp).toLocaleString()}
                      </div>
                    </div>
                    
                    {/* Metadata */}
                    {activity.ip_address && (
                      <div className="mt-2 flex items-center text-xs text-gray-500">
                        <MapPin className="w-3 h-3 mr-1" />
                        {activity.ip_address}
                      </div>
                    )}
                    
                    {/* Additional details */}
                    {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                        <pre className="text-xs text-gray-600 overflow-x-auto">
                          {JSON.stringify(activity.metadata, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            <span className="text-sm text-gray-700">
              Page <span className="font-medium">{page}</span> of{' '}
              <span className="font-medium">{totalPages}</span>
            </span>
            
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeActivityPage;
